import { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useMoney } from '../../../lib/money/useMoney.js';
import { Button } from '../../../components/ui/button.js';
import { Icon } from '../../../components/ui/icon.js';
import { WinCelebration } from '../effects/WinCelebration.js';
import { playSfx } from '../effects/sound.js';
import { useMinesRound } from './useMinesRound.js';
import { useGuestGate } from '../useGuestGate.js';
import { MinesClimbStrip } from './MinesClimbStrip.js';
import { cappedMultiplierBp, payoutMinor } from './multiplier.js';
import { usePieceArt } from '../assets/usePieceArt.js';
import { MINES_PIECES, VIEW_ASPECT } from './minesArt.js';
import { MinesArtBoard, type MinesCellView } from './MinesArtBoard.js';

const GRID = 25;
const COLS = 5;

// Cashout celebration intensity tiers (multiplierBp). Higher tier → denser confetti + brighter chime.
const MINES_BIG_WIN_BP = 50000n; // ×5 → top-tier celebration
const MINES_TIER2_BP = 30000n; // ×3
const MINES_TIER1_BP = 18000n; // ×1.8

// Duration of the "charge" build-up on a tile before its result is shown.
const CHARGE_MS = 300;
// Delay between each mine reveal in the bust cascade.
const CASCADE_STEP_MS = 90;

function celebrationTier(bp: bigint): number {
  if (bp >= MINES_BIG_WIN_BP) return 3;
  if (bp >= MINES_TIER2_BP) return 2;
  if (bp >= MINES_TIER1_BP) return 1;
  return 0;
}

interface Props {
  /** Default mines count + bet are owned by GamePage's bet panel; passed in. */
  bet: bigint;
  minesCount: number;
  /** Reports the current round id (null when idle) so the page can wire the fairness panel. */
  onRoundIdChange?: (roundId: string | null) => void;
  /** The server's cashout clamp for THIS agent's currency, from the mines config. */
  maxWinMinor: bigint;
}

export function MinesGameView({ bet, minesCount, onRoundIdChange, maxWinMinor }: Props) {
  const { t } = useTranslation('originals');
  const guest = useGuestGate();
  const round = useMinesRound({ enabled: !guest.isGuest });
  const { fromMinor } = useMoney(round.currency);
  const reduced = useReducedMotion() ?? false;

  // The board's 3D art: undefined while it loads, null if any piece failed (the plain grid then).
  const art = usePieceArt('mines', MINES_PIECES);

  const tiles = useMemo(() => Array.from({ length: GRID }, (_, i) => i), []);
  const revealedSet = useMemo(() => new Set(round.revealedTiles), [round.revealedTiles]);
  const minePositions = round.minePositions ?? [];
  const mineSet = useMemo(() => new Set(minePositions), [round.minePositions]);

  useEffect(() => {
    onRoundIdChange?.(round.roundId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.roundId]);

  const isActive = round.status === 'active';
  const confirming = round.status === 'confirming';
  const busted = round.status === 'busted';
  const cashed = round.status === 'cashed';
  const settled = busted || cashed;
  const revealCount = round.revealedTiles.length;

  // ── Charge build-up ──────────────────────────────────────────────────────
  // The optimistic-lock (reveal-pending) appends the tile to revealedTiles the
  // instant we tap, so we cannot rely on round state to gate the "charge" beat.
  // We track the charging tile locally and suppress its gem/bomb face until the
  // build-up window elapses — letting the heartbeat tension land first.
  const [chargingTile, setChargingTile] = useState<number | null>(null);
  const chargeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (chargeTimer.current) clearTimeout(chargeTimer.current);
  }, []);

  // ── Escalating gem-pop pitch ─────────────────────────────────────────────
  useEffect(() => {
    if (isActive && revealCount > 0) {
      // Pitch escalates more aggressively (×1.5) than reveal count for a steeper climb.
      playSfx('tilePop', Math.round((revealCount - 1) * 1.5));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revealCount]);

  // ── Dramatic bust: shake + boom, then staggered mine cascade ──────────────
  const [shake, setShake] = useState(false);
  const [redFlash, setRedFlash] = useState(false);
  // Ordered uncover sequence: the tapped mine first (explosion centre), then the
  // rest. We uncover the first `cascadeCount` of this order.
  const [cascadeOrder, setCascadeOrder] = useState<number[]>([]);
  const [cascadeCount, setCascadeCount] = useState(0);
  useEffect(() => {
    if (round.status !== 'busted') {
      setCascadeCount(0);
      setCascadeOrder([]);
      setShake(false);
      setRedFlash(false);
      return;
    }
    playSfx('mineExplode');
    setShake(true);
    setRedFlash(true);
    setChargingTile(null);
    if (chargeTimer.current) clearTimeout(chargeTimer.current);

    const flashTimer = setTimeout(() => setRedFlash(false), 360);
    const shakeTimer = setTimeout(() => setShake(false), 520);

    const mines = round.minePositions ?? [];
    const revealed = new Set(round.revealedTiles);
    const hit = mines.find((m) => revealed.has(m));
    const order = hit != null ? [hit, ...mines.filter((m) => m !== hit)] : [...mines];
    setCascadeOrder(order);
    setCascadeCount(order.length > 0 ? 1 : 0);

    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let n = 1; n < order.length; n++) {
      timers.push(
        setTimeout(() => {
          setCascadeCount((c) => Math.max(c, n + 1));
          playSfx('mineCascade', n);
        }, n * CASCADE_STEP_MS),
      );
    }
    return () => {
      clearTimeout(flashTimer);
      clearTimeout(shakeTimer);
      for (const tm of timers) clearTimeout(tm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.status]);

  // ── Scaled cashout celebration ───────────────────────────────────────────
  const cashedWin = cashed && BigInt(round.payout || '0') > 0n;
  const tier = celebrationTier(BigInt(round.multiplierBp || '0'));
  useEffect(() => {
    if (cashed) {
      playSfx('coin');
      playSfx('ascendChime', tier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [round.status]);

  const onTile = (i: number) => {
    if (!isActive || round.busy || revealedSet.has(i) || chargingTile !== null) return;
    // Build-up: heartbeat tightens with tension (reveal count), then result lands.
    setChargingTile(i);
    playSfx('heartbeat', revealCount);
    if (chargeTimer.current) clearTimeout(chargeTimer.current);
    chargeTimer.current = setTimeout(() => setChargingTile(null), reduced ? 80 : CHARGE_MS);
    void round.reveal(i);
  };

  // Escalating board intensity: 0 (idle) → 1 (max) tied to safe reveals.
  const safeCount = settled
    ? round.revealedTiles.filter((p) => !mineSet.has(p)).length
    : revealCount;
  const intensity = Math.min(safeCount / 12, 1);

  // Server-authoritative bet for the live/settled round; the climb strip already
  // prefers it over the panel bet. Both the ×mult and the amount must be clamped
  // at the server's max win — never promise a cashout the server would clip.
  const roundBet = BigInt(round.bet || '0');
  const roundBp = BigInt(round.multiplierBp || '0');
  const currentMult = Number(cappedMultiplierBp(roundBet, roundBp, maxWinMinor)) / 10000;
  const cashoutLabel = t('mines.cashoutMult', {
    amount: fromMinor(payoutMinor(roundBet, roundBp, maxWinMinor)),
    mult: `×${currentMult.toFixed(2)}`,
  });

  // Near-miss exhale: after cashout, which UN-revealed tile was a mine the player
  // narrowly avoided? Pick the unrevealed mine adjacent to the most safe reveals,
  // falling back to the first unrevealed mine. Server-provided positions only.
  const nearMiss = useMemo<number | null>(() => {
    if (!cashed) return null;
    const unrevealedMines = minePositions.filter((m) => !revealedSet.has(m));
    if (unrevealedMines.length === 0) return null;
    const adjacency = (idx: number) => {
      const r = Math.floor(idx / COLS);
      const c = idx % COLS;
      let n = 0;
      for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]] as const) {
        const nr = r + dr;
        const nc = c + dc;
        if (nr < 0 || nr >= COLS || nc < 0 || nc >= COLS) continue;
        if (revealedSet.has(nr * COLS + nc)) n++;
      }
      return n;
    };
    return unrevealedMines.reduce((best, m) => (adjacency(m) > adjacency(best) ? m : best), unrevealedMines[0]!);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashed, round.minePositions, round.revealedTiles]);

  const escapedMines = cashed ? minePositions.length : 0;

  const pulsePeriod = Math.max(0.45, 1.4 - revealCount * 0.07);

  const cells: MinesCellView[] = tiles.map((i) => {
    const isCharging = chargingTile === i;
    const inRevealed = revealedSet.has(i);
    const isMineHere = mineSet.has(i);
    // During the bust cascade only the first N of the ordered sequence are uncovered.
    const cascadeIndex = busted ? cascadeOrder.indexOf(i) : -1;
    const bomb = busted ? cascadeIndex !== -1 && cascadeIndex < cascadeCount : settled && isMineHere;
    return {
      charging: isCharging,
      // A tile shows its gem face only after the charge window passes.
      gem: inRevealed && !isMineHere && !isCharging,
      bomb,
      hit: busted && inRevealed && isMineHere,
      nearMiss: nearMiss === i,
      disabled: !isActive || round.busy || inRevealed || chargingTile !== null,
    };
  });

  return (
    <div className="relative w-full p-3 sm:p-5">
      {/* The climb: upcoming multiplier steps + live survival odds. */}
      <MinesClimbStrip
        bet={isActive ? BigInt(round.bet || '0') : bet}
        minesCount={isActive ? round.minesCount : minesCount}
        revealedSafe={isActive ? revealCount : 0}
        active={isActive}
        currency={round.currency}
        nextBpStr={round.nextMultiplierBp}
        maxWinMinor={maxWinMinor}
      />

      {/* Board frame / panel: the 3D tray when its art loaded, the plain grid if it failed. */}
      <motion.div
        data-mines-board=""
        animate={
          shake && !reduced
            ? { x: [0, -12, 11, -9, 7, -4, 0], y: [0, 6, -5, 4, -2, 0] }
            : { x: 0, y: 0 }
        }
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={[
          'relative mx-auto originals-grid-fit [--orig-reserve:398px]',
          art === null
            ? 'overflow-hidden rounded-2xl border border-[var(--border-strong)] bg-[var(--bg-sunken)] p-3 sm:p-4'
            : 'rounded-[4%]',
        ].join(' ')}
      >
        {/* Escalating glow (grows + pulses with safe reveals): inside the plain panel, behind the tray. */}
        {isActive && (
          <motion.div
            aria-hidden="true"
            className={['pointer-events-none absolute z-0', art === null ? 'inset-0' : '-inset-[6%] rounded-full blur-2xl'].join(' ')}
            animate={
              reduced
                ? { opacity: 0.15 + intensity * 0.4 }
                : { opacity: [0.12 + intensity * 0.3, 0.28 + intensity * 0.5, 0.12 + intensity * 0.3] }
            }
            transition={
              reduced
                ? { duration: 0 }
                : { repeat: Infinity, duration: pulsePeriod, ease: 'easeInOut' }
            }
            style={
              art === null
                ? {
                    boxShadow: `inset 0 0 ${40 + intensity * 80}px ${10 + intensity * 30}px var(--primary)`,
                    background: `radial-gradient(circle at 50% 50%, transparent ${60 - intensity * 25}%, var(--primary-glow) 130%)`,
                  }
                : { background: `radial-gradient(closest-side, color-mix(in oklab, var(--primary) ${35 + intensity * 45}%, transparent) 0%, transparent 100%)` }
            }
          />
        )}

        {/* Red danger flash on bust. */}
        <AnimatePresence>
          {redFlash && (
            <motion.div
              key="red-flash"
              aria-hidden="true"
              className={['pointer-events-none absolute inset-0 z-20 bg-[var(--danger)]', art === null ? '' : 'rounded-[4%] mix-blend-screen'].join(' ')}
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0.55, 0] }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.36, ease: 'easeOut' }}
            />
          )}
        </AnimatePresence>

        {art ? (
          <div className="relative z-10">
            <MinesArtBoard art={art} cells={cells} onTile={onTile} label={t('mines.reveal')} dim={busted} reduced={reduced} />
          </div>
        ) : art === undefined ? (
          // The tray's own shape while its pictures load, so nothing moves when they arrive.
          <div className="relative z-10 w-full" style={{ aspectRatio: String(VIEW_ASPECT) }} />
        ) : (
          <div
            className={[
              'relative z-10 grid gap-2 sm:gap-2.5 transition-[filter] duration-500',
              busted ? 'saturate-[0.35] brightness-[0.7]' : '',
            ].join(' ')}
            style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
          >
            {tiles.map((i) => {
              const { charging: isCharging, gem: safeRevealed, bomb: showBomb, hit: hitMine, disabled } = cells[i]!;

              return (
                <motion.button
                  key={i}
                  type="button"
                  data-tile={i}
                  disabled={disabled}
                  onClick={() => onTile(i)}
                  aria-label={t('mines.reveal')}
                  initial={false}
                  animate={
                    isCharging && !reduced
                      ? { scale: [1, 0.86, 0.9, 0.84], opacity: [1, 0.7, 0.85, 0.65] }
                      : showBomb
                        ? hitMine
                          ? { scale: [1, 1.25, 1], rotate: [0, -6, 6, 0] }
                          : { scale: [0.8, 1.05, 1] }
                        : safeRevealed
                          ? { scale: [0.85, 1.08, 1] }
                          : { scale: 1, opacity: 1 }
                  }
                  transition={
                    isCharging
                      ? { duration: CHARGE_MS / 1000, ease: 'easeInOut' }
                      : { duration: 0.32, ease: [0.34, 1.56, 0.64, 1] }
                  }
                  className={[
                    'relative aspect-square rounded-xl grid place-items-center select-none',
                    'border transition-colors',
                    isCharging
                      ? 'border-[var(--primary)] bg-[var(--primary)]/20 shadow-[0_0_18px_var(--primary-glow)]'
                      : showBomb
                        ? hitMine
                          ? 'bg-[var(--danger)]/30 border-[var(--danger)] shadow-[0_0_18px_var(--danger)]'
                          : 'bg-[var(--danger)]/15 border-[var(--danger)]/70'
                        : safeRevealed
                          ? 'bg-[var(--primary)]/15 border-[var(--primary)] shadow-[0_0_14px_var(--primary)]'
                          : 'bg-[var(--bg-elevated)] border-[var(--border)] hover:border-[var(--primary)]/60 active:scale-95',
                    // Near-miss highlight after cashout.
                    nearMiss === i ? 'border-[var(--warning)] shadow-[0_0_20px_var(--warning)]' : '',
                  ].join(' ')}
                >
                  {isCharging ? (
                    <motion.span
                      aria-hidden="true"
                      className="block h-2.5 w-2.5 rounded-full bg-[var(--primary)]"
                      animate={reduced ? {} : { scale: [1, 1.8, 1], opacity: [0.6, 1, 0.6] }}
                      transition={{ repeat: Infinity, duration: 0.42, ease: 'easeInOut' }}
                    />
                  ) : showBomb ? (
                    <Icon name="bomb" size={22} className="text-[var(--danger)]" />
                  ) : safeRevealed ? (
                    <Icon name="gem" size={22} className="text-[var(--primary)] drop-shadow-[0_0_6px_var(--primary)]" />
                  ) : null}

                  {/* Near-miss warning marker (the bomb you barely dodged). */}
                  {nearMiss === i && (
                    <motion.span
                      className="absolute -top-1.5 -right-1.5 grid h-5 w-5 place-items-center rounded-full bg-[var(--warning)] text-[var(--bg)]"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 360, damping: 14 }}
                    >
                      <Icon name="bomb" size={12} />
                    </motion.span>
                  )}
                </motion.button>
              );
            })}
          </div>
        )}

        {/* Bust beat overlay. */}
        <AnimatePresence>
          {busted && (
            <motion.div
              key="bust-beat"
              className="pointer-events-none absolute inset-x-0 bottom-3 z-20 flex justify-center"
              initial={{ opacity: 0, y: 14, scale: 0.8 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.18, type: 'spring', stiffness: 280, damping: 16 }}
            >
              <span className="rounded-full border border-[var(--danger)] bg-[var(--danger)]/25 px-4 py-1.5 text-base font-extrabold tracking-wide text-[var(--danger)] shadow-[0_0_20px_var(--danger)]">
                {t('mines.boom')}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        <WinCelebration
          show={cashedWin}
          amountMinor={round.payout}
          multiplier={currentMult.toFixed(2)}
          big={tier >= 2}
          currency={round.currency}
        />
      </motion.div>

      {confirming && (
        <p role="status" className="mt-3 text-center text-[12px] text-[var(--text-secondary)]">
          {t('ORIG-020', { ns: 'errors' })}
        </p>
      )}
      {round.error && <p className="mt-3 text-center text-[12px] text-[var(--danger)]">{round.error}</p>}

      {/* Cash-Out / Bet CTA — anchored cleanly under the board. Reserve a stable height so the
          readout/afterglow beats appearing per state never reflow the card (no layout jump). */}
      <div className="mx-auto mt-4 max-w-[460px] min-h-[112px]">
        {isActive ? (
          <motion.div
            animate={reduced ? {} : { scale: [1, 1.025 + intensity * 0.02, 1] }}
            transition={{ repeat: Infinity, duration: pulsePeriod, ease: 'easeInOut' }}
          >
            <Button
              size="lg"
              glow
              fullWidth
              variant="danger"
              disabled={round.busy || revealCount === 0 || chargingTile !== null}
              onClick={() => {
                playSfx('coin');
                void round.cashout();
              }}
            >
              <Icon name="wallet" size={18} />
              {revealCount === 0 ? t('mines.cashout', { amount: fromMinor(bet) }) : cashoutLabel}
            </Button>
          </motion.div>
        ) : (
          <>
            <Button
              size="lg"
              glow
              fullWidth
              disabled={guest.isGuest ? false : round.busy || confirming}
              onClick={() => {
                if (guest.isGuest) {
                  guest.signIn();
                  return;
                }
                playSfx('click');
                void round.open(Number(bet), minesCount);
              }}
            >
              <Icon name="play" size={18} />
              {settled ? t('tryAgain') : t('mines.start')}
            </Button>
            {guest.isGuest && (
              <p className="mt-2 text-center text-[12px] text-[var(--text-secondary)]" role="status">
                {t('guest.signInToPlay')}
              </p>
            )}
          </>
        )}

        {/* Cashout afterglow beats: escaped-N-mines + near-miss exhale. */}
        <AnimatePresence>
          {cashed && (
            <motion.div
              key="cashout-beat"
              className="mt-2.5 flex flex-col items-center gap-1 text-center"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.2 }}
            >
              <span className="text-[13px] font-semibold text-[var(--success)]">
                {t('mines.escaped', { count: escapedMines })}
              </span>
              {nearMiss !== null && (
                <span className="text-[12px] text-[var(--warning)]">{t('mines.nearMiss')}</span>
              )}
            </motion.div>
          )}
          {busted && (
            <motion.p
              key="busted-msg"
              className="mt-2.5 text-center text-[12px] text-[var(--text-secondary)]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ delay: 0.3 }}
            >
              {t('mines.busted')}
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
